/** @format */

import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { setUserToken, UserToken } from "../../auth/authSlice";
import { fetchTokenWithRefreshToken as fetchHwbTokenWithRefreshToken } from "../../auth/hwbAuth/hwbService";
import { fetchTokenWithRefreshToken as fetchBBTokenWithRefreshToken } from "../../auth/bbOauth/bbOauthService";
import { setAddNotifications } from "../../features/notifications/notificationsSlice";
import { RootState } from "../store";
import i18next from "i18next";

// baseQuery where endpoints and headers are set
const baseQuery = fetchBaseQuery({
  baseUrl:
    process.env.REACT_APP_RESOURCES_API_URL ??
    "https://36h5qfaq9c.execute-api.eu-west-2.amazonaws.com/dev/",
  prepareHeaders: (headers, { getState, extra, endpoint, type, forced }) => {
    const rootState = getState() as RootState;
    const token = rootState.auth?.userToken?.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
  },
});

//baseQuery wrapped in retry
//const baseQueryWithRetry = retry(baseQuery, { maxRetries: 5 });

const baseQueryWithReAuthentication: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let response = await baseQuery(args, api, extraOptions);

  if (response.error) {
    const rootState = api.getState() as RootState;
    const userToken = rootState.auth?.userToken;

    // If expired, refresh token to attempt to resolve query error
    if (userToken && new Date(userToken.expires) < new Date()) {
      const saveNewToken = (newToken: UserToken | void) => {
        if (newToken) {
          console.log("new token");
          api.dispatch(setUserToken(newToken));
        } else {
          // Clear expired token if no new token is recieved
          api.dispatch(
            setAddNotifications({
              date: new Date().toISOString(),
              id: "userTokenClear",
              title: i18next.t("Error updating token"),
              msg: i18next.t("User token cleared from storage"),
              type: "info",
            }),
          );
          console.error("No new token provided, user token cleared");
          api.dispatch(setUserToken(undefined));
          localStorage.removeItem("BL_CMS_STATE");
        }
      };

      const clearFailedToken = (error: unknown) => {
        api.dispatch(
          setAddNotifications({
            date: new Date().toISOString(),
            id: "userTokenClear",
            title: i18next.t("Error updating token"),
            msg: i18next.t("User token cleared from storage"),
            type: "info",
          }),
        );
        console.error(error);
        api.dispatch(setUserToken(undefined));
        localStorage.removeItem("BL_CMS_STATE");
      };

      switch (userToken.service) {
        case "hwb":
          await fetchHwbTokenWithRefreshToken(userToken.refreshToken).then(
            saveNewToken,
            clearFailedToken,
          );
          break;
        case "bitbucket":
          await fetchBBTokenWithRefreshToken(userToken.refreshToken).then(
            saveNewToken,
            clearFailedToken,
          );
          break;
      }

      // Reattempt original query
      response = await baseQuery(args, api, extraOptions);
    }
  }

  return response;
};

// createApi called api which handles tags Resources and User.
// aditional apis will be injected.
export const api = createApi({
  reducerPath: `api`,
  baseQuery: baseQueryWithReAuthentication,
  tagTypes: [
    "Resource",
    "User",
    "Lesson",
    "SubLesson",
    "Card",
    "ResourceStatus",
  ],
  endpoints: (builder) => ({
    getStatus: builder.query({
      query: () => "/status",
    }),
  }),
});

export const { useGetStatusQuery } = api;
